<template>
  <page-template :is-loading="isLoading" :data-exists="!!proxyTemplates">
    <template #pageHeadline>
      {{ $t('proxyTemplate.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="proxyTemplates"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="proxy-templates-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewProxyTemplate()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateProxyTemplate"
        @delete-row-button-clicked="openDeleteProxyTemplateModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <rbz-modal-dialog
        :show="showDeleteProxyTemplateModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteProxyTemplate"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteProxyTemplateModal = false"
        @closeModal="showDeleteProxyTemplateModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedProxyTemplate.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { DocumentName, ProxyTemplate } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ProxyTemplatesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('proxyTemplate.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getProxyTemplatePath(item);
          },
        },
        {
          id: 'static-ip-rate-limit',
          title: this.$t('proxyTemplate.listPage.staticIpRateLimit'),
          fieldNames: ['limit_req_rate', 'limit_req_burst'],
          displayFunction: (item: ProxyTemplate) => {
            return [
              this.$t('proxyTemplate.listPage.staticIpRateLimitRate', {
                value: item['limit_req_rate'],
              }),
              this.$t('proxyTemplate.listPage.staticIpRateLimitBurst', {
                value: item['limit_req_burst'],
              }),
            ].join('\n');
          },
          classes: 'width-200px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'proxy-timeout',
          title: this.$t('proxyTemplate.listPage.proxyTimeout'),
          fieldNames: ['proxy_connect_timeout', 'proxy_send_timeout', 'proxy_read_timeout'],
          displayFunction: (item: ProxyTemplate) => {
            return [
              this.$t('proxyTemplate.listPage.proxyTimeoutConnect', {
                value: item['proxy_connect_timeout'],
              }),
              this.$t('proxyTemplate.listPage.proxyTimeoutSend', {
                value: item['proxy_send_timeout'],
              }),
              this.$t('proxyTemplate.listPage.proxyTimeoutRead', {
                value: item['proxy_read_timeout'],
              }),
            ].join('\n');
          },
          classes: 'width-150px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'advanced-configuration',
          title: this.$t('proxyTemplate.listPage.advancedConfig'),
          displayFunction: (item: ProxyTemplate) => {
            return item.advanced_configuration.length > 0
              ? this.$t('common.yes')
              : this.$t('common.no');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getProxyTemplatePath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      proxyTemplates: [],
      pageLoadingCounter: null,

      showDeleteProxyTemplateModal: false,
      selectedProxyTemplate: null,

      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Proxy Templates List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadProxyTemplates();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('proxyTemplate.plural'),
        singular: this.$t('proxyTemplate.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    getNewProxyTemplate() {
      return this.getNewPath('proxy-templates');
    },

    getProxyTemplatePath(proxyTemplate: ProxyTemplate) {
      return this.getEditPath('proxy-templates', proxyTemplate.id);
    },

    editProxyTemplate(proxyTemplate: ProxyTemplate) {
      this.navigateTo(this.getProxyTemplatePath(proxyTemplate));
    },

    async duplicateProxyTemplate(proxyTemplate: ProxyTemplate) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/proxy-templates/${proxyTemplate.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalProxyTemplate = getOriginalResponse.data;
      const proxyTemplateToAdd = _.cloneDeep(originalProxyTemplate);
      proxyTemplateToAdd.id = DatasetsUtils.generateUUID2();
      proxyTemplateToAdd.name = Utils.generateUniqueEntityName(
        proxyTemplateToAdd.name,
        _.map(this.proxyTemplates, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/proxy-templates/${proxyTemplateToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: proxyTemplateToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editProxyTemplate(proxyTemplateToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteProxyTemplate() {
      this.setPageLoadingStatus(true);
      const proxyTemplateId = this.selectedProxyTemplate.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/proxy-templates/${proxyTemplateId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.proxyTemplates = _.filter(this.proxyTemplates, (proxyTemplate) => {
          return proxyTemplate.id !== proxyTemplateId;
        });
      }
      this.showDeleteProxyTemplateModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('proxy-templates', 'json', this.proxyTemplates);
    },

    openDeleteProxyTemplateModal(proxyTemplate: ProxyTemplate) {
      this.showDeleteProxyTemplateModal = true;
      this.selectedProxyTemplate = proxyTemplate;
    },

    async loadProxyTemplates() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/proxy-templates`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.proxyTemplates = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
      });
      const serverGroups = response?.data?.items || [];
      const referencedIds: string[] = [];
      _.forEach(serverGroups, (serverGroup) => {
        referencedIds.push(serverGroup['proxy_template']);
      });
      this.referencedIds = _.uniq(referencedIds);
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(proxyTemplate: ProxyTemplate) {
      return (
        this.isProxyTemplateProtected(proxyTemplate) ||
        this.isProxyTemplateReferenced(proxyTemplate)
      );
    },

    isProxyTemplateProtected(proxyTemplate: ProxyTemplate): boolean {
      return proxyTemplate.id.startsWith('__');
    },

    isProxyTemplateReferenced(proxyTemplate: ProxyTemplate): boolean {
      return this.referencedIds.includes(proxyTemplate.id);
    },

    deleteTooltip(proxyTemplate: ProxyTemplate): string {
      if (this.isProxyTemplateProtected(proxyTemplate)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isProxyTemplateReferenced(proxyTemplate)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },
  },
});
</script>
